import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=2000&q=80";

const contactInfo = [
  {
    icon: MapPin,
    title: "Siège Social",
    lines: ["190 Avenue Lamine Gueye", "Thiès, Sénégal"],
  },
  {
    icon: Phone,
    title: "Téléphone",
    lines: ["+221 77 547 27 60"],
    link: "tel:+221775472760",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["contact@agrabat.sn"],
    link: "mailto:contact@agrabat.sn",
  },
  {
    icon: Clock,
    title: "Horaires",
    lines: ["Lundi - Vendredi: 8h - 18h", "Samedi: 8h - 13h"],
  },
];

const locations = [
  {
    title: "Siège Social AGRABAT",
    address: "190 Avenue Lamine Gueye, Thiès",
    mapQuery: "190+Avenue+Lamine+Gueye,+Thies,+Senegal",
  },
  {
    title: "Site Production Agglos",
    address: "Sortie N°2 Autoroute, Thiès Keur MADARO",
    mapQuery: "14.7610556,-16.8662500",
  },
  {
    title: "SILIKA GRANULATS",
    address: "Darou Khoudoss zone ICS",
    mapQuery: "Darou+Khoudoss,+Senegal",
  },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message envoyé avec succès! Nous vous répondrons sous peu.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO 
        title="Contact - Devis Agglos, Parpaings, Granulats"
        description="Contactez AGRABAT pour vos commandes d'agglos, parpaings, briques et granulats. Téléphone: +221 77 547 27 60. Email: contact@agrabat.sn. 190 Avenue Lamine Gueye, Thiès, Sénégal."
        keywords="contact AGRABAT, devis agglo Sénégal, commander parpaing Thiès, fournisseur béton Dakar, granulats silex prix"
        url="https://agrabat.sn/contact"
      />
      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="contact-hero"
      >
        <div className="absolute inset-0 bg-[#0A1320]/80" />
        <div className="relative z-10 text-center px-4">
          <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
            Restons en contact
          </p>
          <h1 className="font-oswald text-5xl md:text-7xl font-bold text-white uppercase tracking-tight">
            Contactez-Nous
          </h1>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-slate-50 p-6 border-l-4 border-[#1E3A5F] hover:border-[#FFD100] transition-colors"
                data-testid={`contact-info-${index}`}
              >
                <info.icon className="w-8 h-8 text-[#FFD100] mb-4" />
                <h3 className="font-oswald text-lg font-semibold text-[#1E3A5F] uppercase mb-2">
                  {info.title}
                </h3>
                {info.lines.map((line, idx) => (
                  info.link ? (
                    <a
                      key={idx}
                      href={info.link}
                      className="block text-slate-600 hover:text-[#FFD100] transition-colors"
                    >
                      {line}
                    </a>
                  ) : (
                    <p key={idx} className="text-slate-600">{line}</p>
                  )
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
                Envoyez-nous un message
              </p>
              <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#1E3A5F] uppercase tracking-tight mb-8">
                Formulaire de Contact
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                      Nom Complet *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                      placeholder="Votre nom"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                      placeholder="votre@email.com"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                      placeholder="+221 XX XXX XX XX"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-sm font-medium text-slate-700">
                      Sujet *
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                      placeholder="Objet de votre message"
                      data-testid="contact-input-subject"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F] min-h-[150px]"
                    placeholder="Décrivez votre demande..."
                    data-testid="contact-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#1E3A5F] text-white hover:bg-[#162B47] rounded-none py-4 font-bold uppercase tracking-wider"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="spinner" />
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Envoyer le Message
                    </span>
                  )}
                </Button>
              </form>
            </div>

            {/* Locations */}
            <div>
              <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
                Nos Emplacements
              </p>
              <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#1E3A5F] uppercase tracking-tight mb-8">
                Sites de Production
              </h2>

              <div className="space-y-6">
                {locations.map((location, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 border-l-4 border-[#1E3A5F]"
                    data-testid={`location-${index}`}
                  >
                    <h3 className="font-oswald text-lg font-semibold text-[#1E3A5F] uppercase mb-2">
                      {location.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{location.address}</p>
                    <div className="aspect-video bg-slate-200 overflow-hidden map-container">
                      <iframe
                        title={location.title}
                        src={`https://www.google.com/maps?q=${location.mapQuery}&output=embed`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
